import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, ExternalLink, Globe, Check, ToggleLeft, ToggleRight, Edit2, Save, X, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PublicCatalogWidget() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [org, setOrg] = useState(null);
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/organization");
      setOrg(data);
      setSlugInput(data.slug || "");
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { load(); }, []);

  if (!org) return null;

  const baseUrl = window.location.origin;
  const publicUrl = `${baseUrl}/boutique`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const toggle = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", { public_catalog_enabled: !org.public_catalog_enabled });
      setOrg(data);
      toast.success(data.public_catalog_enabled ? "Catalogue public activé" : "Catalogue public désactivé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const saveSlug = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/organization", { slug: slugInput });
      setOrg(data);
      setEditingSlug(false);
      toast.success("URL mise à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  // Télécharge le QR en PNG haute résolution (utilisable pour impression A4,
  // affiche, autocollant…).
  const downloadQR = () => {
    const canvas = document.querySelector('[data-testid="public-qr"] canvas');
    if (!canvas) return toast.error("QR introuvable");
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-catalogue-${org.slug || "boutique"}.png`;
    a.click();
    toast.success("QR téléchargé");
  };

  // Ouvre une vue d'impression dédiée (format A6 / autocollant carton) avec
  // QR XXL + URL + accroche. La cliente n'a qu'à cliquer "Imprimer" et coller.
  // Utilise un Blob URL au lieu de document.write() pour fiabilité maximale
  // sur mobile (iOS Safari, Chrome Android) où document.write peut produire
  // une page blanche.
  const printPoster = (mode = "single") => {
    // mode = "single" → 1 affiche A6 par page
    // mode = "x4"     → 4 affiches identiques sur 1 page A4 (économie papier)
    const canvas = document.querySelector('[data-testid="public-qr"] canvas');
    if (!canvas) return toast.error("QR introuvable");
    const qrDataUrl = canvas.toDataURL("image/png");
    // Nettoyage du nom : supprime "Hébergement" si présent (au cas où l'org
    // en DB prod n'a pas encore été mise à jour).
    const rawName = org.name || "Vénus Médical";
    const orgName = rawName.replace(/\s*hébergement\s*/gi, "").trim() || "Vénus Médical";
    const baseline = org.description || "Matériels et Consommables Médicaux Dentaires";

    const posterHtml = `
<div class="poster">
  <div class="header">
    <div class="brand">${orgName}</div>
    <div class="baseline">${baseline}</div>
  </div>
  <div class="qr"><img src="${qrDataUrl}" alt="QR Code"/></div>
  <div class="footer-block">
    <div class="cta">Scannez pour commander en ligne</div>
    <div class="url">${publicUrl}</div>
  </div>
  <div class="footer">Livraison à Dakar et au Sénégal</div>
</div>`;

    const isX4 = mode === "x4";
    const pageSize = isX4 ? "A4 portrait" : "A6 portrait";
    const gridStyle = isX4
      ? "display:grid; grid-template-columns:1fr 1fr; grid-template-rows:1fr 1fr; gap:8mm; height:calc(100vh - 80px);"
      : "height:calc(100vh - 80px); padding: 16px;";
    const qrSize = isX4 ? "150px" : "200px";
    const brandSize = isX4 ? "15px" : "18px";

    const html = `<!doctype html>
<html lang="fr"><head><meta charset="utf-8"><title>QR Catalogue — ${orgName}</title>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  @page { size: ${pageSize}; margin: ${isX4 ? "10mm" : "6mm"}; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a0f1a; background: #f7f0f3; min-height: 100vh; }
  .sheet { ${gridStyle} background: #fff; }
  .poster {
    border: 3px solid #C2569B; border-radius: 14px; padding: 14px;
    text-align: center; background: #fff; height: 100%;
    display: flex; flex-direction: column; align-items: center; justify-content: space-between;
    break-inside: avoid; gap: 8px;
  }
  .brand { font-weight: 800; font-size: ${brandSize}; letter-spacing: 0.5px; color: #8E3A6D; line-height: 1.1; }
  .baseline { font-size: 10px; color: #666; margin-top: 4px; text-transform: uppercase; letter-spacing: 1px; line-height: 1.3; }
  .qr { padding: 8px; background: #fff; border-radius: 8px; }
  .qr img { display: block; width: ${qrSize}; height: ${qrSize}; }
  .cta { font-size: 13px; font-weight: 700; margin: 0 0 4px; }
  .url { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 10px; color: #444; word-break: break-all; padding: 4px 8px; background: #f6f0f3; border-radius: 6px; }
  .footer { font-size: 9px; color: #999; }

  /* Barre d'action sticky en haut */
  .toolbar {
    position: sticky; top: 0; z-index: 100;
    background: #1a0f1a; padding: 12px 16px;
    display: flex; gap: 10px; align-items: center; justify-content: space-between;
    box-shadow: 0 2px 12px rgba(0,0,0,0.2);
  }
  .toolbar h1 { color: #fff; font-size: 14px; margin: 0; font-weight: 600; }
  .toolbar button {
    padding: 10px 16px; font-size: 14px; border: 0; border-radius: 8px;
    font-weight: 700; cursor: pointer; min-height: 44px;
  }
  .btn-print { background: #C2569B; color: #fff; }
  .btn-back { background: #fff; color: #1a0f1a; }
  .btn-back:active, .btn-print:active { transform: scale(0.96); }

  @media print {
    body { padding: 0 !important; background: #fff !important; }
    .toolbar { display: none !important; }
    .sheet { background: #fff !important; }
  }
</style></head><body>
<div class="toolbar">
  <button class="btn-back" onclick="window.close()">← Retour</button>
  <h1>${isX4 ? "4 affiches A4" : "Affiche A6"}</h1>
  <button class="btn-print" onclick="window.print()">🖨️ Imprimer</button>
</div>
<div class="sheet">
  ${isX4 ? posterHtml.repeat(4) : posterHtml}
</div>
</body></html>`;

    // Création d'un Blob URL : approche fiable cross-browser (desktop + mobile)
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const w = window.open(blobUrl, "_blank");
    if (!w) {
      URL.revokeObjectURL(blobUrl);
      return toast.error("Le navigateur a bloqué l'ouverture. Autorisez les popups pour ce site.");
    }
    // Libère le blob après 60 secondes (laisse le temps à la page de charger)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  return (
    <div className="surface overflow-hidden" data-testid="public-catalog-widget">
      <div className="px-4 py-3 border-b border-[var(--border-line)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[var(--primary-soft,#F8E8F1)] flex items-center justify-center">
            <Globe size={16} className="text-[var(--primary-dark,#8E3A6D)]" />
          </div>
          <div>
            <div className="font-heading font-bold">Mon catalogue public</div>
            <div className="label-mono">Commande sans login</div>
          </div>
        </div>
        <button
          data-testid="toggle-public-catalog"
          onClick={toggle}
          disabled={saving || !isAdmin}
          className={`flex items-center gap-2 text-sm font-semibold ${isAdmin ? "" : "hidden"}`}
          title={org.public_catalog_enabled ? "Désactiver" : "Activer"}
        >
          {org.public_catalog_enabled
            ? <><ToggleRight size={28} className="text-[var(--primary,#C2569B)]"/><span className="text-[var(--primary-dark,#8E3A6D)]">Activé</span></>
            : <><ToggleLeft size={28} className="text-[var(--text-muted)]"/><span className="text-[var(--text-muted)]">Désactivé</span></>}
        </button>
      </div>

      <div className="p-4 grid md:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="space-y-3 min-w-0">
          <div>
            <div className="label-mono mb-1.5">URL publique à partager</div>
            {editingSlug ? (
              <div className="flex items-center gap-2">
                <span className="font-data text-xs text-[var(--text-muted)] hidden sm:inline">{baseUrl}/boutique/</span>
                <input
                  data-testid="slug-input"
                  className="input-base flex-1"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  placeholder="ma-pharmacie"
                />
                <button data-testid="save-slug" onClick={saveSlug} disabled={saving} className="btn-primary px-3"><Save size={14}/></button>
                <button onClick={() => { setEditingSlug(false); setSlugInput(org.slug); }} className="btn-ghost px-3"><X size={14}/></button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 bg-[var(--bg-soft,#f7f7f7)] border border-[var(--border-line)] rounded-lg px-3 py-2 font-data text-sm truncate" data-testid="public-url">
                  {publicUrl}
                </div>
                <button data-testid="edit-slug" onClick={() => setEditingSlug(true)} className="btn-ghost px-3" title="Modifier"><Edit2 size={14}/></button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="copy-public-url" onClick={copy} className="btn-primary flex items-center gap-2">
              {copied ? <><Check size={14}/>Copié</> : <><Copy size={14}/>Copier le lien</>}
            </button>
            <a data-testid="open-public-url" href={publicUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost flex items-center gap-2">
              <ExternalLink size={14}/>Ouvrir
            </a>
          </div>
          <div className="text-xs text-[var(--text-muted)] leading-relaxed">
            Partagez ce lien sur WhatsApp, Facebook, vos cartes de visite. Les pharmacies et particuliers peuvent commander en ligne sans créer de compte. Les commandes apparaissent dans <b>Commandes Clients</b> en statut « Brouillon ».
          </div>
        </div>

        <div className="bg-white border border-[var(--border-line)] rounded-xl p-3 flex flex-col items-center self-center">
          <div data-testid="public-qr" className="bg-white p-3 rounded-lg">
            <QRCodeCanvas value={publicUrl} size={168} bgColor="#ffffff" fgColor="#000000" level="L" includeMargin={true} />
          </div>
          <div className="label-mono mt-2 text-center">Scanner pour commander</div>
          <div className="grid grid-cols-3 gap-1.5 mt-3 w-full">
            <button
              data-testid="download-qr"
              onClick={downloadQR}
              className="btn-ghost flex items-center justify-center gap-1 text-[11px] py-1.5 px-1"
              title="Télécharger le QR au format PNG haute résolution"
            >
              <Download size={11}/>PNG
            </button>
            <button
              data-testid="print-qr-poster"
              onClick={() => printPoster("single")}
              className="btn-primary flex items-center justify-center gap-1 text-[11px] py-1.5 px-1"
              title="Imprimer 1 affiche A6 (carton)"
            >
              <Printer size={11}/>A6
            </button>
            <button
              data-testid="print-qr-poster-x4"
              onClick={() => printPoster("x4")}
              className="btn-primary flex items-center justify-center gap-1 text-[11px] py-1.5 px-1"
              title="Imprimer 4 affiches identiques sur 1 page A4 (économie de papier)"
            >
              <Printer size={11}/>A4×4
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
